import * as PIXI from 'pixi.js'

import { Dimension } from '../../../layout/constants'
import { createDefaultStyle } from '../../../style/parser'
import textLayout from '../layout'
import tokenize, { registerTextRule } from '../tokenizer'
import { measureText, selectResolutionIndex } from '../utils'

import TextDrawer from './TextDrawer'

const defaultStyle = createDefaultStyle()

export default class Text extends PIXI.Sprite {
  constructor() {
    const drawer = new TextDrawer()
    super(drawer.texture)

    this.drawer = drawer
    this.layout = { width: 0, height: 0 }
    this.drawerText = null
    this.drawerStyle = null
    this.drawerWidth = 0
    this.drawerHeight = 0

    this._text = null
    this.resolutionIndex = 0
  }

  set text(value) {
    if (this._text === value) {
      return
    }

    this._text = value
    this.tokens = tokenize(value)
    for (let token of this.tokens) {
      token.setParent(this)
    }
    this.applyLayout()
  }

  applyLayout(width) {
    const style = this.style || defaultStyle
    for (let token of this.tokens) {
      token.setStyle(style)
    }
    const size = textLayout(this.tokens, style, { width })
    this.layout.width = width === undefined ? size.width : width
    this.layout.height = size.height
  }

  draw() {
    this.texture = this.drawer.draw(
      this.drawerStyle,
      this.drawerWidth,
      this.drawerHeight,
      this.tokens,
      this.worldTransform.a,
    )
  }

  /**
   * Layouter API realisation to draw background after layout
   */
  onLayoutCompleted({ style }) {
    const { width, height } = this.layout
    if (
      this.drawerText === this._text &&
      this.drawerStyle === style &&
      this.drawerWidth === width &&
      this.drawerHeight === height
    ) {
      return
    }
    this.drawerText = this._text
    this.drawerStyle = style
    this.drawerWidth = width
    this.drawerHeight = height
    this.draw()
  }

  /**
   * Layouter API realisation to perform custom text layout
   */
  mudraLayout(style, width, height) {
    this.applyLayout(width)
    return this.layout
  }

  /**
   * Layouter API realisation to store layout size and use it for background drawing
   */
  setLayoutSize(dimension, value) {
    if (dimension === Dimension.WIDTH && this.layout.width !== value) {
      this.applyLayout(value)
    }
  }

  render(renderer) {
    // if the object is not visible or the alpha is 0 then no need to render this element
    if (!this.visible || this.worldAlpha <= 0 || !this.renderable) {
      return
    }

    // SIC: micro optimization for not call drawer.draw() and swap texture when resolutionIndex was not changed.
    const index = selectResolutionIndex(this.worldTransform.a)
    if (this.resolutionIndex !== index) {
      this.resolutionIndex = index
      this.draw()
    }

    this._render(renderer)
  }

  destroy() {
    this.drawer.destroy()
    super.destroy({ texture: false, baseTexture: false })
  }
}

Text.measureText = measureText
Text.registerTextRule = registerTextRule
